import React from 'react'
import { Flow, FlowName, FlowScreenElement } from '../../../../../@types/flow-types'
import { AGENT_FLAT_SHARE_ROOM_RENT_FLOW_PATH } from '../../../../../services/routes/routes'
import {
  Gender,
  RealEstateCategory,
  RealEstateFeatures,
  Salutation,
  SegmentationType,
  TransactionType,
} from '../../../../../@types/types'
import { agentFlatShareRoomRentSchema } from './agentFlatShareRoomRentSchema'
import {
  SCREEN_ADDRESS,
  SCREEN_API_SEARCH_DATA,
  SCREEN_BASIC_INFO,
  SCREEN_CONTACT_INFO,
  SCREEN_COSTS,
  SCREEN_FLAT_SHARE_ROOM_PROPERTIES,
  SCREEN_MULTIMEDIA,
  SCREEN_OBJECT_NUMBERS,
  SCREEN_OTHER_FACILITIES,
  SCREEN_WHO_WE_LOOK_FOR,
  SCREENS,
} from '../../../../../services/domain/wizardScreens'
import Address from '../../../../../components/pages/insertionPage/screens/address/Address'
import ContactInfo from '../../../../../components/pages/insertionPage/screens/contactInfo/ContactInfo'
import Multimedia from '../../../../../components/pages/insertionPage/screens/multimediaNew/Multimedia'

import WhoWeLookForScreen from '../../../../../components/pages/insertionPage/screens/whoWeLookFor/WhoWeLookForScreen'
import FlatShareRoomProperties from '../../../../../components/pages/insertionPage/screens/flatShareRoomProperties/FlatShareRoomProperties'
import ResidentialCostsRent from '../../../../../components/pages/insertionPage/screens/costs/wrappers/residential/ResidentialCostsRent'
import { MultimediaUploaderType } from '../../../../../@types/multimedia-types'
import {
  adTitle,
  estateFeatures,
  floorAndTotalFloor,
  intermediateRent,
  minimumTermOfLease,
  moveInDateAndFreeUntilDate,
} from '../../../../../components/pages/insertionPage/screens/otherFacilities/wrapper/otherFacilitiesFieldsWrapper'
import OtherFacilities from '../../../../../components/pages/insertionPage/screens/otherFacilities/OtherFacilities'
import { getDefaultClasses } from '../../../../../utils/style-utils'
import BasicInfo from '../../../../../components/pages/insertionPage/screens/basicInfo/BasicInfo'
import {
  bathroomField,
  livingSpaceField,
  priceField,
  roomField,
  roomSizeField,
} from '../../../../../components/pages/insertionPage/screens/basicInfo/wrappers/basicInfoFieldsWrapper'
import { PriceFieldType } from '../../../../../components/pages/insertionPage/screens/basicInfo/fields/priceField/PriceField'
import { courtageInformationToApi } from '../../../../../api/realEstate/converters/toApi/estate/courtageInformation/courtageInformationToApi'
import ApiSearchData from '../../../../../components/pages/insertionPage/screens/apiSearchData/ApiSearchData'
import { apiSearchDataToApi } from '../../../../../api/realEstate/converters/toApi/estate/apiSearchData/apiSearchDataToApi'
import { ObjectNumbersScreen } from '../../../../../components/pages/insertionPage/screens/objectNumber/ObjectNumbersScreen'
import { priceToApiMonthlyRent } from '../../../../../api/realEstate/converters/toApi/estate/price/priceToApi'

const agentFlatShareRoomRentFlowScreens: FlowScreenElement[] = [
  {
    screen: SCREENS[SCREEN_ADDRESS],
    element: (props) => <Address {...props} isPriceOverviewLinkDisplayed={false} />,
  },
  {
    screen: SCREENS[SCREEN_OBJECT_NUMBERS],
    element: (props) => <ObjectNumbersScreen {...props} />,
  },
  {
    screen: SCREENS[SCREEN_BASIC_INFO],
    element: (props) => (
      <BasicInfo
        {...props}
        screenTooltipKey='tooltip.screens.basicInfo.apartmentHouseOrSTARent'
        properties={[
          livingSpaceField('full'),
          roomField(getDefaultClasses('left')),
          priceField(PriceFieldType.COLD_RENT, getDefaultClasses('right', { hasNoGridItem: true })),
          bathroomField(getDefaultClasses('left')),
          roomSizeField(),
        ]}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_COSTS],
    element: (props) => (
      <ResidentialCostsRent
        {...props}
        showMoveInDate={false}
        showCourtage={true}
        screenTooltipKey='tooltip.screens.costs.allRent'
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_MULTIMEDIA],
    element: (props) => (
      <Multimedia
        {...props}
        doCloseMultimediaStatusMessage={props.doCloseMultimediaStatusMessage!}
        allowMultimediaLinks={true}
        allowedTypesToDisplay={[
          MultimediaUploaderType.PICTURE,
          MultimediaUploaderType.FLOOR_PLAN,
          MultimediaUploaderType.DOCUMENT,
          MultimediaUploaderType.VIDEO,
        ]}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_OTHER_FACILITIES],
    element: (props) => (
      <OtherFacilities
        {...props}
        screenTooltipKey='tooltip.screens.otherFacilities.allRent'
        properties={[
          adTitle(),
          estateFeatures([
            RealEstateFeatures.BATH_HAS_SHOWER,
            RealEstateFeatures.BATH_HAS_WC,
            RealEstateFeatures.BATH_HAS_BATHTUB,
            RealEstateFeatures.GUEST_TOILET,
            RealEstateFeatures.TV_CABLE,
            RealEstateFeatures.DISHWASHER,
            RealEstateFeatures.DSL,
            RealEstateFeatures.WASHING_MACHINE,
          ]),
          minimumTermOfLease(getDefaultClasses('left')),
          floorAndTotalFloor('right'),
          intermediateRent(),
          moveInDateAndFreeUntilDate(),
        ]}
      />
    ),
  },
  { screen: SCREENS[SCREEN_WHO_WE_LOOK_FOR], element: (props) => <WhoWeLookForScreen {...props} /> },
  {
    screen: SCREENS[SCREEN_FLAT_SHARE_ROOM_PROPERTIES],
    element: (props) => <FlatShareRoomProperties {...props} />,
  },
  {
    screen: SCREENS[SCREEN_API_SEARCH_DATA],
    element: (props) => <ApiSearchData {...props} />,
  },
  {
    screen: SCREENS[SCREEN_CONTACT_INFO],
    element: (props) => <ContactInfo {...props} hasContactManager={true} />,
  },
]

export const agentFlatShareRoomRentFlow: Flow = {
  flowInformation: {
    owner: 'wizard.navigation.header.agent',
    estateType: 'wizard.navigation.header.agentFlatShareRoom',
    offerType: 'wizard.navigation.header.rents',
    path: AGENT_FLAT_SHARE_ROOM_RENT_FLOW_PATH,
    name: FlowName.AGENT_FLAT_SHARE_ROOM_RENT,
    validationSchema: agentFlatShareRoomRentSchema,
    metaData: {
      category: RealEstateCategory.FLAT_SHARE_ROOM,
      segmentation: SegmentationType.AGENT,
      transaction: TransactionType.RENT,
    },
    priceToApiMapper: priceToApiMonthlyRent,
    courtageToApiMapper: courtageInformationToApi,
    apiSearchDataToApiMapper: apiSearchDataToApi,
  },
  defaultStateValues: {
    hideStreetAndStreetNumber: false,
    intermediateRent: false,
    salutation: Salutation.NO_SALUTATION,
    requestedGender: Gender.MALE_OR_FEMALE,
    heatingCostsOption: true,
  },
  flowScreens: agentFlatShareRoomRentFlowScreens,
}
